import * as React from 'react';

import { ComboBox, Select, useComboBoxOptions } from '@rematter/ui-web';
import type { JSONSchema7 } from 'json-schema';

import { ExpressionInput } from '../../../shared/ExpressionInput';
import { NodeConfigProps } from '../../types';

import {
  BusinessEventSource,
  BusinessEventTriggerConfigType,
  getEntitiesByModule,
  getEventCasesForEntity,
  loadBusinessEventDataSchema,
  MODULE_LABELS,
} from './schema';

/**
 * Configuration panel for Business Event triggers.
 * Uses two-level selection: Entity → Event Case
 */
export const BusinessEventTriggerConfig: React.FC<
  NodeConfigProps<BusinessEventTriggerConfigType>
> = ({ config, onUpdate, nodeId }) => {
  // Extract current entity and event case from config
  const currentEntity = config.source?.case || '';
  const currentEventCase = React.useMemo(() => {
    if (!config.source?.value) return '';
    const sourceValue = config.source.value;
    if (typeof sourceValue === 'object' && 'event' in sourceValue) {
      const eventObj = sourceValue.event as { case?: string };
      return eventObj?.case || '';
    }
    return '';
  }, [config.source]);

  const [filterExpression, setFilterExpression] = React.useState<string>(
    config.filterExpression || '',
  );

  // Load entity schema for autocomplete
  const [entitySchema, setEntitySchema] = React.useState<JSONSchema7 | null>(
    null,
  );

  React.useEffect(() => {
    if (currentEntity) {
      setEntitySchema(loadBusinessEventDataSchema(currentEntity));
    } else {
      setEntitySchema(null);
    }
  }, [currentEntity]);

  // Build combined schema for filter expression autocomplete
  const filterEnvironmentSchema = React.useMemo<JSONSchema7>(
    () => ({
      type: 'object',
      properties: {
        trigger: {
          type: 'object',
          title: 'Trigger',
          properties: {
            id: { type: 'string', title: 'Trigger ID' },
            timestamp: { type: 'string', title: 'Timestamp' },
            eventType: { type: 'string', title: 'Event Type' },
            entityId: { type: 'string', title: 'Entity ID' },
            entityType: { type: 'string', title: 'Entity Type' },
          },
        },
        data: entitySchema || { type: 'object', title: 'Event Data' },
      },
    }),
    [entitySchema],
  );

  // Get entities grouped by module
  const entitiesByModule = React.useMemo(() => getEntitiesByModule(), []);

  // Get event cases for the selected entity
  const eventCases = React.useMemo(
    () => (currentEntity ? getEventCasesForEntity(currentEntity) : []),
    [currentEntity],
  );

  // Build entity ComboBox options with proper groups
  const entityOptions = React.useMemo(() => {
    const opts: Array<{
      id: string;
      label: string;
      group: string;
      searchableContent: string;
    }> = [];

    for (const [module, entities] of Object.entries(entitiesByModule)) {
      const groupLabel = MODULE_LABELS[module] || module;

      for (const entity of entities) {
        opts.push({
          id: entity.key,
          label: entity.label,
          group: groupLabel,
          searchableContent: `${entity.label} ${groupLabel}`,
        });
      }
    }

    return opts;
  }, [entitiesByModule]);

  // Use search hook for filtering options
  const { query, onChangeQuery, filteredOptions } = useComboBoxOptions({
    options: entityOptions,
    value: currentEntity,
  });

  // Build event case Select options
  const eventCaseOptions = React.useMemo(
    () =>
      eventCases.map((ec) => ({
        key: ec.key,
        label: ec.label,
      })),
    [eventCases],
  );

  const handleEntityChange = React.useCallback(
    (values: string[]) => {
      const entityKey = values[0];
      // When entity changes, reset the event case
      onUpdate({
        ...config,
        source: entityKey
          ? ({
              case: entityKey,
              value: { event: { case: undefined } },
            } satisfies BusinessEventSource)
          : undefined,
      });
    },
    [config, onUpdate],
  );

  // Get the selection label for the ComboBox
  const getEntitySelection = React.useCallback(
    (ids: string[]) => {
      const entity = entityOptions.find((e) => e.id === ids[0]);
      return entity ? [{ label: entity.label }] : [];
    },
    [entityOptions],
  );

  const handleEventCaseChange = React.useCallback(
    (eventCase: string) => {
      if (!currentEntity) return;

      onUpdate({
        ...config,
        source: {
          case: currentEntity,
          value: {
            event: {
              case: eventCase,
              value: {},
            },
          },
        } satisfies BusinessEventSource,
      });
    },
    [config, currentEntity, onUpdate],
  );

  const handleFilterExpressionChange = React.useCallback(
    (value: string) => {
      setFilterExpression(value);
      onUpdate({ ...config, filterExpression: value });
    },
    [config, onUpdate],
  );

  return (
    <div className="@BusinessEventTriggerConfig space-y-4">
      <div>
        <label className="mb-1 block text-sm font-medium">Entity</label>
        <ComboBox<string>
          placeholder="Select entity..."
          value={currentEntity ? [currentEntity] : []}
          onChange={handleEntityChange}
          options={filteredOptions}
          query={query}
          onChangeQuery={onChangeQuery}
          getSelection={getEntitySelection}
        />
        <p className="mt-1 text-xs text-neutral/60">
          The business entity that will trigger this automation
        </p>
      </div>

      {currentEntity && (
        <div>
          <label className="mb-1 block text-sm font-medium">Event</label>
          <Select
            placeholder="Select event..."
            value={currentEventCase}
            onChange={handleEventCaseChange}
            options={eventCaseOptions}
          />
          <p className="mt-1 text-xs text-neutral/60">
            The specific event type that will trigger the automation
          </p>
        </div>
      )}

      <div>
        <ExpressionInput
          label="Filter Expression"
          value={filterExpression}
          onChange={handleFilterExpressionChange}
          environmentSchema={filterEnvironmentSchema}
          nodeId={nodeId}
          fieldName="filterExpression"
          placeholder="e.g., data.status == 'active'"
          description="Optional expression to filter which events trigger the automation"
        />
      </div>

      <div className="rounded-lg border border-primary/20 bg-primary/10 p-3">
        <p className="text-xs text-primary">
          <strong>Tip:</strong> Use filter expressions to trigger only on
          specific conditions. For example, filter by status or specific field
          values.
        </p>
      </div>
    </div>
  );
};
